import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  Image,
  Button,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Menu } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { useJoin } from '../context/JoinContext';
import logo from '../assets/yetzira.png';

const navLinks = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'The Engine', href: '#exponential' },
  { label: 'The Moat', href: '#moat' },
  { label: 'Infrastructure', href: '#infrastructure' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { openJoin } = useJoin();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href, label) => {
    trackEvent('nav_click', { label, destination: href });
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    onClose();
  };

  const handleJoinClick = (source) => {
    trackEvent('cta_click', { cta: 'join', source });
    openJoin();
    onClose();
  };

  return (
    <Box
      as="nav"
      role="navigation"
      aria-label="Main navigation"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      transition="all 0.3s ease"
      bg={scrolled ? 'rgba(10,11,15,0.95)' : 'transparent'}
      backdropFilter={scrolled ? 'blur(12px)' : 'none'}
      borderBottom={scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none'}
    >
      <Flex
        maxW="1200px"
        mx="auto"
        px={{ base: '24px', md: '48px' }}
        py={4}
        align="center"
        justify="space-between"
      >
        {/* Logo + Wordmark */}
        <HStack
          spacing={3}
          cursor="pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Yetzira.ai home"
        >
          <Image
            src={logo}
            alt="Yetzira logo"
            h={{ base: '32px', md: '36px' }}
            w="auto"
            objectFit="contain"
          />
          <Text
            fontFamily="'Space Grotesk', sans-serif"
            fontWeight="700"
            fontSize="1.25rem"
            color="text.primary"
            letterSpacing="-0.02em"
          >
            Yetzira
            <Text as="span" color="brand.500">
              .ai
            </Text>
          </Text>
        </HStack>

        {/* Desktop Nav */}
        <HStack spacing={8} display={{ base: 'none', md: 'flex' }}>
          {navLinks.map((link) => (
            <Button
              key={link.label}
              variant="ghost"
              color="text.muted"
              fontSize="0.9rem"
              fontWeight="500"
              _hover={{ color: 'text.primary', bg: 'transparent' }}
              onClick={() => handleNavClick(link.href, link.label)}
              p={0}
              h="auto"
            >
              {link.label}
            </Button>
          ))}
        </HStack>

        {/* Desktop CTA */}
        <Box display={{ base: 'none', md: 'block' }}>
          <Button variant="gold" size="sm" onClick={() => handleJoinClick('navbar')}>
            Join us
          </Button>
        </Box>

        {/* Mobile Menu Button */}
        <IconButton
          display={{ base: 'flex', md: 'none' }}
          icon={<Menu size={20} />}
          variant="ghost"
          color="text.primary"
          aria-label="Open menu"
          onClick={onOpen}
          _hover={{ bg: 'whiteAlpha.100' }}
        />
      </Flex>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay bg="rgba(10,11,15,0.8)" backdropFilter="blur(8px)" />
        <DrawerContent bg="bg.800" borderLeft="1px solid rgba(255,255,255,0.06)">
          <DrawerCloseButton color="text.primary" />
          <DrawerHeader>
            <HStack spacing={3}>
              <Image src={logo} alt="Yetzira logo" h="30px" w="auto" objectFit="contain" />
              <Text fontFamily="'Space Grotesk', sans-serif" fontWeight="700" color="text.primary">
                yetzira
                <Text as="span" color="brand.500">
                  .ai
                </Text>
              </Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch" pt={4}>
              {navLinks.map((link) => (
                <Button
                  key={link.label}
                  variant="ghost"
                  color="text.muted"
                  justifyContent="flex-start"
                  _hover={{ color: 'text.primary', bg: 'whiteAlpha.50' }}
                  onClick={() => handleNavClick(link.href, link.label)}
                >
                  {link.label}
                </Button>
              ))}
              <Button variant="gold" mt={4} onClick={() => handleJoinClick('navbar_mobile')}>
                Join us
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
